import { useState } from "react"
import SelectionField from "./SelectionField"
import InputField from "./InputField"
import UploadField from "./UploadField"
import BaseHeadline from "../../../components/general/BaseHeadline"
import BaseSection from "../../../components/layout/BaseSection"
import axios from "axios"
import { CDS } from "../../../api/api"

type optionsType = { label: string; value: string }
type inputType = {
    identifier: string
    label: string
    value: string
    type: string
}

type StepType =
    | { type: "selection"; label: string; identifier: string; options: optionsType[]; submit: boolean }
    | { type: "input"; fields: inputType[]; submit: boolean }
    | { type: "upload"; submit: boolean }

const flowAuftrag: StepType[] = [
    {
        type: "input",
        fields: [
            { identifier: "vorname", label: "Vorname", value: "", type: "text" },
            { identifier: "nachname", label: "Nachname", value: "", type: "text" },
            { identifier: "email", label: "E-Mail", value: "", type: "email" },
            { identifier: "phone", label: "Telefonnummer", value: "", type: "tel" },
        ],
        submit: false,
    },
    {
        type: "input",
        fields: [
            { identifier: "company", label: "Unternehmen", value: "", type: "text" },
            { identifier: "subject", label: "Betreff", value: "", type: "text" },
            { identifier: "body", label: "Deine Nachricht", value: "", type: "textarea" },
        ],
        submit: false,
    },
    {
        type: "selection",
        label: "Welchen Projektrahmen hast du dir vorgestellt?",
        identifier: "projectSize",
        options: [
            { label: "Keine Angabe", value: "keine Angabe" },
            { label: "< 10.000 €", value: "bis 10.000 €" },
            { label: "10.000 - 25.000 €", value: "10.000 - 25.000 €" },
            { label: "25.000 - 50.000 €", value: "25.000 - 50.000 €" },
            { label: "50.000 - 100.000 €", value: "50.000 - 100.000 €" },
            { label: "> 100.000 €", value: "mehr als 100.000 €" },
        ],
        submit: true,
    },
]

const flowHallo: StepType[] = [
    {
        type: "input",
        fields: [
            { identifier: "vorname", label: "Vorname", value: "", type: "text" },
            { identifier: "nachname", label: "Nachname", value: "", type: "text" },
            { identifier: "email", label: "E-Mail", value: "", type: "email" },
        ],
        submit: false,
    },
    {
        type: "input",
        fields: [
            { identifier: "subject", label: "Betreff", value: "", type: "text" },
            { identifier: "body", label: "Deine Nachricht", value: "", type: "textarea" },
        ],
        submit: true,
    },
]

const flowBewerbung: StepType[] = [
    {
        type: "selection",
        label: "Für welche Position möchtest du dich bewerben?",
        identifier: "position",
        options: [
            { label: "Designer", value: "Designer" },
            { label: "Developer - Frontend", value: "Developer - Frontend" },
            { label: "Developer - Backend", value: "Developer - Backend" },
            { label: "Developer - Fullstack", value: "Developer - Fullstack" },
            { label: "Developer - DevOps", value: "Developer - DevOps" },
            { label: "Marketing", value: "Marketing" },
            { label: "Vertrieb", value: "Vertrieb" },
        ],
        submit: false,
    },
    {
        type: "selection",
        label: "Für welche Art der Beschäftigung möchtest du dich bewerben?",
        identifier: "jobType",
        options: [
            { label: "Praktikum", value: "Praktikum" },
            { label: "Werkstudent", value: "Werkstudent" },
            { label: "Teilzeit", value: "Teilzeit" },
            { label: "Vollzeit", value: "Vollzeit" },
            { label: "Freelancer", value: "Freelancer" },
        ],
        submit: false,
    },
    {
        type: "input",
        fields: [
            { identifier: "name", label: "Name", value: "", type: "text" },
            { identifier: "email", label: "E-Mail", value: "", type: "email" },
            { identifier: "phone", label: "Telefon (optional)", value: "", type: "tel" },
        ],
        submit: false,
    },
    {
        type: "input",
        fields: [{ identifier: "message", label: "Erzähle uns etwas über dich", value: "", type: "textarea" }],
        submit: false,
    },
    {
        type: "upload",
        submit: true,
    },
]

export default function ContactField() {
    const [step, setStep] = useState(0)
    const [selectedFlow, setSelectedFlow] = useState<StepType[] | null>(null)
    const [formData, setFormData] = useState<Record<string, string>>({})
    const [uploadedFiles, setUploadedFiles] = useState<File[] | null>(null)

    const mainOptions: optionsType[] = [
        { label: "Ein Projekt in Auftrag geben", value: "Auftrag anfragen" },
        { label: "Hallo sagen :)", value: "Hallo Sagen" },
        { label: "Bei Wicode bewerben", value: "Bewerbung" },
    ]

    const handleMainSelect = (value: string) => {
        switch (value) {
            case "Auftrag anfragen":
                setSelectedFlow(flowAuftrag)
                break
            case "Hallo Sagen":
                setSelectedFlow(flowHallo)
                break
            case "Bewerbung":
                setSelectedFlow(flowBewerbung)
                break
        }

        setFormData({
            contactReason: value,
        })
    }

    const handleNext = () => setStep((prev) => prev + 1)
    const handleBack = () => setStep((prev) => prev - 1)

    const currentStep = selectedFlow?.[step - 1]

    const handleInputChange = (key: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }))
    }

    const handleSelectChange = (key: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }))
    }

    const handleFileChange = (files: FileList | null) => {
        if (files) {
            setUploadedFiles(Array.from(files))
        } else {
            setUploadedFiles(null)
        }
    }

    const [errorMsg, setErrorMsg] = useState(false)
    const [errorText, setErrorText] = useState("")
    const [thankYouMsg, setThankYouMsg] = useState(false)

    const handleSubmit = async () => {
        try {
            const isBewerbung = formData.contactReason === "Bewerbung"

            // Validation based on flow type
            if (isBewerbung) {
                if (!formData.name || !formData.email || !formData.message || !formData.position || !formData.jobType) {
                    setErrorMsg(true)
                    setErrorText("Bitte fülle alle Pflichtfelder aus!")
                    return
                }

                if (!uploadedFiles || uploadedFiles.length === 0) {
                    setErrorMsg(true)
                    setErrorText("Bitte lade mindestens ein Dokument hoch!")
                    return
                }

                // Create FormData for file upload
                const formDataToSend = new FormData()
                formDataToSend.append("name", formData.name)
                formDataToSend.append("email", formData.email)
                formDataToSend.append("contactReason", formData.contactReason)
                formDataToSend.append("message", formData.message)
                formDataToSend.append("position", formData.position)
                formDataToSend.append("jobType", formData.jobType)

                if (formData.phone) {
                    formDataToSend.append("phone", formData.phone)
                }

                // Append files
                uploadedFiles.forEach((file) => {
                    formDataToSend.append("documents", file)
                })

                await axios
                    .post(CDS.API_WITH_ATTACHMENTS, formDataToSend, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            "X-Auth": CDS.BEWERBUNGS_KEY,
                        },
                    })
                    .then((_) => {
                        setThankYouMsg(true)
                        setErrorMsg(false)
                    })
                    .catch((err) => {
                        setErrorMsg(true)
                        setThankYouMsg(false)
                        setErrorText(err.response?.data?.DisplayMessage ?? "Es gab einen Fehler. Die Kontaktanfrage konnte nicht gesendet werden.")
                    })
            } else {
                // Original validation for other flows
                if (
                    formData.vorname === "" ||
                    formData.nachname === "" ||
                    formData.email === "" ||
                    formData.contactReason === "" ||
                    formData.body === ""
                ) {
                    setErrorMsg(true)
                    setErrorText("Bitte fülle alle Pflichtfelder aus!")
                    return
                }

                await axios
                    .post(CDS.API, formData, {
                        headers: {
                            "Content-Type": "application/json",
                            "X-Auth": CDS.GENERAL_KEY,
                        },
                    })
                    .then((_) => {
                        setThankYouMsg(true)
                        setErrorMsg(false)
                    })
                    .catch((err) => {
                        setErrorMsg(true)
                        setThankYouMsg(false)
                        setErrorText(err.response?.data?.DisplayMessage ?? "Es gab einen Fehler. Die Kontaktanfrage konnte nicht gesendet werden.")
                    })
            }
        } catch (error: any) {
            console.error("Error sending message", error)
            setErrorMsg(true)
            // Check if error has response data with DisplayMessage
            const displayMessage = error?.response?.data?.DisplayMessage || "Es gab einen Fehler. Die Kontaktanfrage konnte nicht gesendet werden."
            setErrorText(displayMessage)
        }
    }

    return (
        <BaseSection flexCol classProps="px-4 md:px-8 xl:px-0 min-h-screen justify-center md:mt-[2rem]">
            <BaseHeadline
                headline={"Lassen Sie uns über Sie sprechen!"}
                // sublabel={"Let's Talk"}
                width="w-full"
                // position=""
                classProps="!text-4xl"
                // secondaryTextSizeProps="text-7xl sm:text-[7.5rem] lg:!text-[10rem] leading-[6rem] sm:leading-[7rem]"
            />

            {step === 0 && (
                <SelectionField
                    label="Wieso möchtest du uns kontaktieren?"
                    option={mainOptions}
                    onSelect={(value) => handleMainSelect(value)}
                    selectedValue={formData["contactReason"]}
                    onNext={() => setStep(1)}
                    submit={false}
                    onSubmit={handleSubmit}
                />
            )}

            {currentStep?.type === "input" && (
                <InputField
                    option={currentStep.fields}
                    onChange={handleInputChange}
                    values={formData}
                    onNext={handleNext}
                    onBack={handleBack}
                    submit={currentStep.submit}
                    onSubmit={handleSubmit}
                />
            )}

            {currentStep?.type === "selection" && (
                <SelectionField
                    label={currentStep.label}
                    option={currentStep.options}
                    onSelect={(value) => handleSelectChange(currentStep.identifier, value)}
                    selectedValue={formData[currentStep.identifier]}
                    onNext={handleNext}
                    onBack={handleBack}
                    submit={currentStep.submit}
                    onSubmit={handleSubmit}
                />
            )}

            {currentStep?.type === "upload" && (
                <UploadField
                    onChange={handleFileChange}
                    files={uploadedFiles}
                    onNext={handleNext}
                    onBack={handleBack}
                    submit={currentStep.submit}
                    onSubmit={handleSubmit}
                />
            )}

            {errorMsg && <div className="mt-8 text-sm leading text-error">! {errorText} :(</div>}
            {selectedFlow && step > selectedFlow.length && thankYouMsg && <div className="mt-8 text-2xl">Danke für deine Anfrage!</div>}
        </BaseSection>
    )
}
